/**
 * 消息提示响应式 Composable
 * 提供消息状态管理和用户提示功能
 * 纯函数逻辑已移至 @/utils/message-utils
 */

import { ref, computed, onBeforeUnmount, type ComputedRef } from 'vue';
import { ElMessage } from 'element-plus';
import {
  createQueuedMessage,
  isDuplicateMessage,
  isMessageExpired,
  calculateMessageStats,
  mergeMessageConfig,
  type MessageConfig,
  type MessageType,
  type QueuedMessage,
  type MessageStats
} from '@/utils/notify';

export interface MessageOptions extends Partial<MessageConfig> {}

export interface UseMessageReturn {
  // 状态
  messageQueue: ReturnType<typeof ref<QueuedMessage[]>>;
  messageStats: ComputedRef<MessageStats>;

  // 基础消息方法
  showSuccess: (message: string, options?: MessageOptions) => void;
  showError: (message: string, options?: MessageOptions) => void;
  showWarning: (message: string, options?: MessageOptions) => void;
  showInfo: (message: string, options?: MessageOptions) => void;

  // 高级消息方法
  showLoading: (message: string, options?: MessageOptions) => () => void;
  showProgress: (message: string, progress: number, options?: MessageOptions) => void;
  showConfirm: (message: string, options?: MessageOptions) => Promise<boolean>;

  // 队列管理
  clearQueue: () => void;
  clearExpiredMessages: () => void;
  removeMessage: (id: string) => void;
  
  // 配置管理
  setGlobalConfig: (config: Partial<MessageConfig>) => void;
  resetGlobalConfig: () => void;
}

export function useMessage(): UseMessageReturn {
  // 消息队列
  const messageQueue = ref<QueuedMessage[]>([]);
  const maxQueueSize = 100;
  
  // 全局配置
  const globalConfig = ref<Partial<MessageConfig>>({
    duration: 3000,
    showClose: true,
    center: false
  });

  // 消息统计
  const messageStats = computed(() => calculateMessageStats(messageQueue.value));

  /**
   * 显示消息的通用方法
   */
  const showMessage = (
    type: MessageType,
    message: string,
    options: MessageOptions = {}
  ): void => {
    const config = mergeMessageConfig(type, { ...globalConfig.value, ...options });
    const queuedMessage = createQueuedMessage(type, message, config);

    // 检查重复消息
    if (config.preventDuplicate && isDuplicateMessage(queuedMessage, messageQueue.value)) {
      return;
    }

    // 添加到队列
    addToQueue(queuedMessage);

    // 显示消息
    ElMessage({
      type: type as any,
      message,
      duration: config.duration,
      showClose: config.showClose,
      onClose: () => removeMessage(queuedMessage.id)
    });
  };

  /**
   * 成功消息
   */
  const showSuccess = (message: string, options: MessageOptions = {}) => {
    showMessage('success', message, options);
  };

  /**
   * 错误消息
   */
  const showError = (message: string, options: MessageOptions = {}) => {
    showMessage('error', message, options);
  };

  /**
   * 警告消息
   */
  const showWarning = (message: string, options: MessageOptions = {}) => {
    showMessage('warning', message, options);
  };

  /**
   * 信息消息
   */
  const showInfo = (message: string, options: MessageOptions = {}) => {
    showMessage('info', message, options);
  };

  /**
   * 加载消息
   */
  const showLoading = (message: string, options: MessageOptions = {}): (() => void) => {
    const config = mergeMessageConfig('info', { ...globalConfig.value, duration: 0, ...options });
    const queuedMessage = createQueuedMessage('info', message, config);

    addToQueue(queuedMessage);

    const messageInstance = ElMessage({
      type: 'info',
      message,
      duration: 0,
      showClose: false
    });

    // 返回关闭函数
    return () => {
      messageInstance.close();
      removeMessage(queuedMessage.id);
    };
  };

  /**
   * 进度消息
   */
  const showProgress = (message: string, progress: number, options: MessageOptions = {}) => {
    const progressMessage = `${message} ${Math.round(progress)}%`;
    showInfo(progressMessage, options);
  };

  /**
   * 确认消息
   */
  const showConfirm = async (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      ElMessage({
        type: 'warning',
        message,
        duration: 0,
        showClose: true,
        onClose: () => resolve(false)
      });
      
      // 这里可以扩展为真正的确认对话框
      setTimeout(() => resolve(true), 3000);
    });
  };

  /**
   * 添加到队列
   */
  const addToQueue = (message: QueuedMessage) => {
    messageQueue.value.unshift(message);
    
    // 限制队列大小
    if (messageQueue.value.length > maxQueueSize) {
      messageQueue.value = messageQueue.value.slice(0, maxQueueSize);
    }
  };

  /**
   * 移除消息
   */
  const removeMessage = (id: string) => {
    const index = messageQueue.value.findIndex(msg => msg.id === id);
    if (index > -1) {
      messageQueue.value.splice(index, 1);
    }
  };

  /**
   * 清空队列
   */
  const clearQueue = () => {
    messageQueue.value = [];
  };

  /**
   * 清除过期消息
   */
  const clearExpiredMessages = () => {
    messageQueue.value = messageQueue.value.filter(msg => !isMessageExpired(msg));
  };

  /**
   * 设置全局配置
   */
  const setGlobalConfig = (config: Partial<MessageConfig>) => {
    globalConfig.value = { ...globalConfig.value, ...config };
  };

  /**
   * 重置全局配置
   */
  const resetGlobalConfig = () => {
    globalConfig.value = {
      duration: 3000,
      showClose: true,
      center: false
    };
  };

  // 定期清理过期消息（每30秒）
  const cleanupInterval = setInterval(clearExpiredMessages, 30000);

  // 清理定时器（防止内存泄漏）
  try {
    onBeforeUnmount(() => {
      clearInterval(cleanupInterval);
    });
  } catch (error) {
    // 如果不在组件上下文中，忽略生命周期钩子
    console.warn('useMessage: 不在组件上下文中，跳过生命周期钩子注册');
  }

  return {
    // 状态
    messageQueue,
    messageStats,

    // 基础消息方法
    showSuccess,
    showError,
    showWarning,
    showInfo,

    // 高级消息方法
    showLoading,
    showProgress,
    showConfirm,

    // 队列管理
    clearQueue,
    clearExpiredMessages,
    removeMessage,
    
    // 配置管理
    setGlobalConfig,
    resetGlobalConfig
  };
}
